import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ACTIVE_ORG_CHANGED_EVENT,
  clearActiveOrg,
  getActiveOrgId,
  getActiveOrgName,
  setActiveOrg as persistActiveOrg,
} from "services/active-org.service";
import { fetchUserOrganisations, UserOrganisation } from "services/orgs.service";

type ActiveOrgContextValue = {
  orgId: number;
  orgName: string | null;
  organisations: UserOrganisation[];
  setActiveOrg: (orgId: number, orgName: string) => void;
  loading: boolean;
};

const ActiveOrgContext = createContext<ActiveOrgContextValue | null>(null);

export function ActiveOrgProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const [orgId, setOrgId] = useState(getActiveOrgId);
  const [orgName, setOrgName] = useState<string | null>(getActiveOrgName);
  const [organisations, setOrganisations] = useState<UserOrganisation[]>([]);
  const [loading, setLoading] = useState(true);

  const syncFromStorage = useCallback(() => {
    setOrgId(getActiveOrgId());
    setOrgName(getActiveOrgName());
  }, []);

  useEffect(() => {
    syncFromStorage();
    window.addEventListener("storage", syncFromStorage);
    window.addEventListener(ACTIVE_ORG_CHANGED_EVENT, syncFromStorage);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(ACTIVE_ORG_CHANGED_EVENT, syncFromStorage);
    };
  }, [syncFromStorage]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      clearActiveOrg();
      setOrganisations([]);
      setOrgId(0);
      setOrgName(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        let token: string;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          if (cancelled) return;
          const err = e as { error?: string; message?: string };
          const isAuthError =
            err.error === "login_required" ||
            err.error === "consent_required" ||
            err.message?.includes("Missing Refresh Token") ||
            err.message?.includes("login_required");
          if (isAuthError) {
            loginWithRedirect({ appState: { returnTo: window.location.pathname } });
            return;
          }
          throw e;
        }
        const orgs = await fetchUserOrganisations(token);
        if (cancelled) return;

        setOrganisations(orgs);

        const current = getActiveOrgId();
        const stillValid = current > 0 && orgs.some((o) => o.orgId === current);

        if (!stillValid) {
          if (orgs.length > 0) {
            const first = orgs[0];
            persistActiveOrg(first.orgId, first.orgName);
            setOrgId(first.orgId);
            setOrgName(first.orgName);
          } else {
            clearActiveOrg();
            setOrgId(0);
            setOrgName(null);
          }
        } else {
          const match = orgs.find((o) => o.orgId === current);
          if (match && getActiveOrgName() !== match.orgName) {
            persistActiveOrg(match.orgId, match.orgName);
          }
          syncFromStorage();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, syncFromStorage]);

  const setActiveOrg = useCallback((id: number, name: string) => {
    persistActiveOrg(id, name);
    setOrgId(id);
    setOrgName(name);
  }, []);

  const value = useMemo(
    () => ({
      orgId,
      orgName,
      organisations,
      setActiveOrg,
      loading,
    }),
    [orgId, orgName, organisations, setActiveOrg, loading]
  );

  return (
    <ActiveOrgContext.Provider value={value}>{children}</ActiveOrgContext.Provider>
  );
}

export function useActiveOrg(): ActiveOrgContextValue {
  const ctx = useContext(ActiveOrgContext);
  if (!ctx) {
    throw new Error("useActiveOrg must be used within ActiveOrgProvider");
  }
  return ctx;
}
